import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { confirmar, textoDeError } from '../components/Dialogo';
import { rolEtiqueta } from '../lib/roles';

/**
 * Administración → PERMISOS (0.55.0): qué SECCIÓN del HUB abre cada rol y, si hace falta, cada persona. (La bitácora de
 * accesos ya se llamaba "Accesos": por eso esto se llama "Permisos".) La lista maestra y las reglas viven en el API
 * (api/src/lib/permisos.ts) y llegan con `GET panel/permisos`; aquí sólo se marcan casillas y se guarda con
 * `PUT panel/permisos` (`updated_by` por subselect, bitácora `cambio:permisos`).
 *
 *  - Admin abre todo, siempre (no sale como casilla). Administración no se le abre a otro rol ni a una persona.
 *  - Un rol que se deja como "lo de siempre" no se guarda: sigue el default del código.
 *  - Una persona con "lista propia" usa ESA lista en vez de la de su rol.
 *  - Lo delicado dentro de cada sección sigue siendo de admin (la lista va plegada abajo).
 */
interface Seccion { id: string; etiqueta: string; descripcion: string; soloAdmin?: boolean }
interface Cuenta { user_id: string; nombre: string; rol: string; propia: boolean; secciones: string[] }
interface Resp {
  secciones: Seccion[]; configurables: string[]; roles: string[]; default: Record<string, string[]>;
  solo_admin_dentro: { seccion: string; que: string }[];
  config: { roles: Record<string, string[]>; personas: Record<string, string[]> };
  por_rol: Record<string, string[]>; cuentas: Cuenta[];
  guardada: boolean; updated_at: string | null; updated_by: string | null;
  descartados?: string[];
}

const igual = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));
const mayus = (t: string) => (t ? t[0].toUpperCase() + t.slice(1) : t);
const fecha = (iso: string | null) => (iso ? new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '');

function Casillas({ secciones, marcadas, onCambio, datos }: { secciones: Seccion[]; marcadas: string[]; onCambio: (l: string[]) => void; datos: string }) {
  return (
    <div className="perm-casillas" data-casillas={datos}>
      {secciones.map((s) => (
        <label key={s.id} className="perm-casilla" data-seccion-casilla={s.id}>
          <input type="checkbox" checked={marcadas.includes(s.id)}
            onChange={(e) => onCambio(e.target.checked ? [...marcadas, s.id] : marcadas.filter((x) => x !== s.id))} />
          <span style={{ minWidth: 0 }}>
            <span style={{ fontWeight: 600 }}>{s.etiqueta}</span>
            <span className="muted" style={{ display: 'block', fontSize: 11.5, lineHeight: 1.35 }}>{s.descripcion}</span>
          </span>
        </label>
      ))}
    </div>
  );
}

export function Permisos({ qs, tenantSel }: { qs: string; tenantSel: string }) {
  const [d, setD] = useState<Resp | null>(null);
  const [roles, setRoles] = useState<Record<string, string[]>>({});
  const [personas, setPersonas] = useState<Record<string, string[] | null>>({});
  const [busca, setBusca] = useState('');
  const [msg, setMsg] = useState<{ t: string; ok: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  const tomar = (r: Resp) => {
    setD(r);
    setRoles(Object.fromEntries(r.roles.map((x) => [x, [...(r.por_rol[x] || [])]])));
    setPersonas(Object.fromEntries(r.cuentas.filter((c) => c.rol !== 'admin').map((c) => [c.user_id, c.propia ? [...c.secciones] : null])));
  };
  useEffect(() => {
    setD(null); setMsg(null);
    api<Resp>('panel/permisos' + qs).then(tomar).catch((e) => setMsg({ t: textoDeError(e, 'No se pudieron leer los permisos.'), ok: false }));
  }, [qs, tenantSel]);

  const configurables = useMemo(() => (d ? d.secciones.filter((s) => d.configurables.includes(s.id)) : []), [d]);
  const etiqueta = (id: string) => d?.secciones.find((s) => s.id === id)?.etiqueta || id;
  const lista = (ids: string[]) => (ids.length ? ids.map(etiqueta).join(', ') : 'ninguna sección (sólo Mi cuenta)');

  if (!d) return msg ? <div role="alert" className="card" style={{ padding: 14, fontSize: 13 }}>{msg.t}</div> : <p className="muted">Cargando…</p>;

  const cuentas = d.cuentas.filter((c) => c.rol !== 'admin');
  const admins = d.cuentas.filter((c) => c.rol === 'admin');
  const rolesCambiados = d.roles.filter((r) => !igual(roles[r] || [], d.por_rol[r] || []));
  const personasCambiadas = cuentas.filter((c) => {
    const ahora = personas[c.user_id] ?? null;
    const antes = c.propia ? c.secciones : null;
    return (ahora === null) !== (antes === null) || (ahora && antes && !igual(ahora, antes));
  });
  const cambios = rolesCambiados.length + personasCambiadas.length;
  const q = busca.trim().toLowerCase();
  const visibles = q ? cuentas.filter((c) => c.nombre.toLowerCase().includes(q) || rolEtiqueta(c.rol).includes(q)) : cuentas;

  const descartarCambios = async () => {
    if (!(await confirmar({ titulo: '¿Descartar los cambios?', peligro: true, confirmar: 'Descartar cambios', cancelar: 'Seguir editando',
      mensaje: 'Las casillas regresan a lo que está guardado. Nada se guarda.' }))) return;
    tomar(d); setMsg(null);
  };

  const guardar = async () => {
    const renglones: string[] = [];
    for (const r of rolesCambiados) renglones.push(`${mayus(rolEtiqueta(r))}: ${lista(roles[r] || [])}.`);
    for (const c of personasCambiadas) {
      const p = personas[c.user_id];
      renglones.push(p ? `${c.nombre}: lista propia — ${lista(p)}.` : `${c.nombre}: vuelve a lo de su rol.`);
    }
    if (!(await confirmar({
      titulo: '¿Guardar los permisos?', confirmar: 'Guardar permisos',
      mensaje: `${renglones.slice(0, 8).join('\n')}${renglones.length > 8 ? `\n… y ${renglones.length - 8} cambios más.` : ''}\n\nLas pantallas que ya están abiertas se acomodan en unos segundos; el menú de cada quien cambia solo.`,
    }))) return;
    setBusy(true); setMsg(null);
    try {
      // Sólo viaja lo que se aparta de lo de siempre (un rol igual al default sigue al default) y las listas propias.
      const cuerpoRoles: Record<string, string[]> = {};
      for (const r of d.roles) if (!igual(roles[r] || [], d.default[r] || [])) cuerpoRoles[r] = roles[r] || [];
      const cuerpoPersonas: Record<string, string[]> = {};
      for (const [id, l] of Object.entries(personas)) if (l) cuerpoPersonas[id] = l;
      const r = await api<Resp>('panel/permisos' + qs, { method: 'PUT', body: JSON.stringify({ roles: cuerpoRoles, personas: cuerpoPersonas }) });
      tomar(r);
      const fuera = r.descartados?.length ? ` ${r.descartados.length} ${r.descartados.length === 1 ? 'cuenta ya no está activa (o es de admin) y se quitó' : 'cuentas ya no están activas (o son de admin) y se quitaron'} de las excepciones.` : '';
      setMsg({ t: `Guardado.${fuera}`, ok: true });
    } catch (e) { setMsg({ t: textoDeError(e, 'No se pudieron guardar los permisos.'), ok: false }); }
    setBusy(false);
  };

  return (
    <div data-seccion="permisos" style={{ display: 'grid', gap: 14, gridTemplateColumns: 'minmax(0, 1fr)', paddingBottom: cambios ? 70 : 0 }}>
      <div className="card" style={{ padding: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Qué sección abre cada rol</div>
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          Lo que no se marca no sale en el menú de esas cuentas, la pantalla las manda a la primera sección que sí abren y el
          servidor se lo niega con su porqué. <strong>Admin</strong> abre todo, siempre{admins.length ? ` (${admins.map((a) => a.nombre).join(', ')})` : ''}.
          {' '}<strong>Administración</strong> (cuentas, roles, precios y estos permisos) es sólo de admin: no se le abre a nadie más.
        </p>
        <div className="muted" style={{ fontSize: 12, marginTop: 8 }} data-permisos-guardado>
          {d.guardada ? `Último cambio: ${fecha(d.updated_at)}${d.updated_by ? ' · ' + d.updated_by : ''}` : 'Todavía nadie los ha cambiado: cada rol abre lo de siempre.'}
        </div>
      </div>

      {d.roles.map((r) => {
        const marcadas = roles[r] || [];
        const deSiempre = igual(marcadas, d.default[r] || []);
        return (
          <div key={r} className="card" style={{ padding: 16 }} data-rol={r}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>{mayus(rolEtiqueta(r))}</span>
              <span className="muted" style={{ fontSize: 12 }} data-rol-estado>{deSiempre ? 'lo de siempre' : 'cambiado'}</span>
              {!deSiempre && (
                <button type="button" className="btn-ghost" style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: 12.5 }} data-rol-siempre
                  onClick={() => setRoles((x) => ({ ...x, [r]: [...(d.default[r] || [])] }))}>Volver a lo de siempre</button>
              )}
            </div>
            <Casillas secciones={configurables} marcadas={marcadas} datos={r} onCambio={(l) => setRoles((x) => ({ ...x, [r]: l }))} />
            {marcadas.length === 0 && (
              <div role="status" style={{ marginTop: 8, padding: '6px 10px', borderRadius: 8, fontSize: 12.5, background: 'var(--warning-bg, #fef3c7)', color: '#92400e' }} data-rol-vacio>
                Sin ninguna sección: estas cuentas sólo podrán abrir «Mi cuenta».
              </div>
            )}
          </div>
        );
      })}

      <div className="card" style={{ padding: 16 }} data-excepciones>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Excepciones por persona</div>
        <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
          Una cuenta con <strong>lista propia</strong> abre lo que marques aquí, sin importar su rol. Las demás abren lo de su rol.
        </p>
        {cuentas.length > 8 && (
          <input type="search" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar una cuenta"
            aria-label="Buscar una cuenta" style={{ maxWidth: 320, marginBottom: 10 }} data-buscar-cuenta />
        )}
        {cuentas.length === 0 && <div className="muted" style={{ fontSize: 13 }}>No hay cuentas activas que no sean de admin.</div>}
        <div style={{ display: 'grid', gap: 8 }}>
          {visibles.map((c) => {
            const propia = personas[c.user_id] ?? null;
            return (
              <div key={c.user_id} className="perm-persona" data-persona={c.user_id}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>{c.nombre}</span>
                  <span className="muted" style={{ fontSize: 12 }}>{rolEtiqueta(c.rol)}</span>
                  <label style={{ marginLeft: 'auto', fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }} data-lista-propia>
                    <input type="checkbox" checked={propia !== null}
                      onChange={(e) => setPersonas((x) => ({ ...x, [c.user_id]: e.target.checked ? [...(roles[c.rol] || d.default[c.rol] || [])] : null }))} />
                    Lista propia
                  </label>
                </div>
                {propia === null
                  ? <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Usa lo de su rol: {lista(roles[c.rol] || d.default[c.rol] || ['resumen', 'fichas'])}.</div>
                  : <div style={{ marginTop: 8 }}><Casillas secciones={configurables} marcadas={propia} datos={c.user_id}
                      onCambio={(l) => setPersonas((x) => ({ ...x, [c.user_id]: l }))} /></div>}
              </div>
            );
          })}
        </div>
      </div>

      <details className="card" style={{ padding: 16 }} data-solo-admin-lista>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Lo que sigue siendo de admin aunque la sección esté abierta</summary>
        <p className="muted" style={{ fontSize: 13 }}>Lo que pide PIN, crea o mueve dinero o valor, junta expedientes o manda datos de pacientes a otra empresa:</p>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, display: 'grid', gap: 4 }}>
          {d.solo_admin_dentro.map((x, i) => <li key={i}><strong>{etiqueta(x.seccion)}</strong> — {x.que}</li>)}
        </ul>
      </details>

      {msg && <div role={msg.ok ? 'status' : 'alert'} style={{ padding: '8px 12px', borderRadius: 10, fontSize: 13, background: msg.ok ? 'var(--success-bg, #d1fae5)' : 'var(--warning-bg, #fef3c7)', color: msg.ok ? '#065f46' : '#92400e' }} data-permisos-msg>{msg.t}</div>}

      {cambios > 0 && (
        <div className="perm-barra" data-sin-guardar>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Hay cambios sin guardar</span>
          <span style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <button type="button" className="btn-ghost" disabled={busy} onClick={descartarCambios} data-descartar-permisos>Descartar</button>
            <button type="button" className="btn-primary" disabled={busy} onClick={guardar} data-guardar-permisos>{busy ? 'Guardando…' : 'Guardar'}</button>
          </span>
        </div>
      )}
    </div>
  );
}
